import type { FrameTransport, HostInfo } from '@casa/bridge';
import { base64ToBytes, bytesToBase64 } from '@casa/util';

/** Electron: exposed by the preload script through `contextBridge`. */
interface ElectronPort {
    post(frame: ArrayBuffer) : void;
    onFrame(listener: (frame: ArrayBuffer) => void) : void;
}

/** Android: the object injected by `WebViewCompat.addWebMessageListener`. */
interface AndroidPort {
    postMessage(message: string | ArrayBuffer) : void;
    onmessage: ((event: { data: unknown }) => void) | null;
}

/** iOS: a `WKScriptMessageHandler`; the shell answers by calling `__casaBridgeReceive`. */
interface IosPort {
    postMessage(message: string) : void;
}

function exactBuffer(frame: Uint8Array) : ArrayBuffer {
    const buffer = frame.buffer as ArrayBuffer;

    return frame.byteOffset === 0 && frame.byteLength === buffer.byteLength
        ? buffer
        : buffer.slice(frame.byteOffset, frame.byteOffset + frame.byteLength);
}

function listeners() {
    const set = new Set<(frame: Uint8Array) => void>();

    return {
        emit(frame: Uint8Array) {
            for (const listener of [...set]) {
                listener(frame);
            }
        },
        on(listener: (frame: Uint8Array) => void) {
            set.add(listener);

            return () => set.delete(listener);
        },
    };
}

/** Finds the frame transport of the shell the app runs in; null in a plain browser. */
export function detectTransport(globals: Record<string, unknown>) : FrameTransport | null {
    const host = globals.casaHost as HostInfo | undefined;
    const incoming = listeners();

    if (host?.shell === 'electron') {
        const port = globals.casaBridge as ElectronPort | undefined;
        if (!port) {
            return null;
        }

        port.onFrame((frame) => incoming.emit(new Uint8Array(frame)));

        return { post: (frame) => port.post(exactBuffer(frame)), onFrame: incoming.on };
    }

    if (host?.shell === 'android') {
        const port = globals.casaBridge as AndroidPort | undefined;
        if (!port) {
            return null;
        }

        const base64 = host.bridgeEncoding === 'base64';
        port.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                incoming.emit(new Uint8Array(event.data));
            } else if (typeof event.data === 'string') {
                incoming.emit(base64ToBytes(event.data));
            }
        };

        return {
            post: (frame) => port.postMessage(base64 ? bytesToBase64(frame) : exactBuffer(frame)),
            onFrame: incoming.on,
        };
    }

    if (host?.shell === 'ios') {
        const webkit = globals.webkit as { messageHandlers?: { casaBridge?: IosPort } } | undefined;
        const port = webkit?.messageHandlers?.casaBridge;
        if (!port) {
            return null;
        }

        globals.__casaBridgeReceive = (frame: string) => incoming.emit(base64ToBytes(frame));

        return { post: (frame) => port.postMessage(bytesToBase64(frame)), onFrame: incoming.on };
    }

    return null;
}
